import { useState } from 'react'
import { ChevronDown, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useContextStore } from '@store/context'
import { useOperateStore } from '@/store/operate'
import { usePermissions } from '@/hooks/usePermissions'
import { useAuth } from '@/providers/AuthProvider'
import {
  DEPLOYMENT_KINDS,
  deploymentsAPI,
  normalizeDeploymentKind,
  type DeploymentKind,
} from '@/services/api/deployments'
import { isConsoleScopedInstance } from '@/config/runtime'
import { toast } from '@/providers/NotificationProvider'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Input } from '@/components/ui/Input'
import { agentSpecAuthoringHref } from '@/utils/contextUrl'
import { cn } from '@/utils/cn'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'

function deploymentKindBadgeClass(kind: DeploymentKind): string {
  switch (kind) {
    case 'sandbox':
      return 'bg-agent-cyan/20 text-agent-cyan'
    case 'development':
      return 'bg-agent-green/20 text-agent-green'
    case 'integration_test':
      return 'bg-agent-violet/20 text-agent-violet'
    case 'staging':
      return 'bg-agent-orange/20 text-agent-orange'
    case 'production':
      return 'bg-destructive/15 text-destructive'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

export default function ContextSwitcher() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [newDeploymentName, setNewDeploymentName] = useState('')
  const [newDeploymentDescription, setNewDeploymentDescription] = useState('')
  const [creatingDeployment, setCreatingDeployment] = useState(false)
  const [pendingKind, setPendingKind] = useState<DeploymentKind | null>(null)
  const [updatingKind, setUpdatingKind] = useState(false)
  const organizations = useContextStore((state) => state.organizations)
  const deployments = useContextStore((state) =>
    state.deployments.filter((item) => item.isAdmin !== true),
  )
  const org = useContextStore((state) => state.org)
  const deploymentId = useContextStore((state) => state.deploymentId)
  const env = useContextStore((state) => state.env)
  const isLoading = useContextStore((state) => state.isLoading)
  const error = useContextStore((state) => state.error)
  const setCurrentSessionId = useOperateStore((state) => state.setCurrentSessionId)
  const setOrg = useContextStore((state) => state.setOrg)
  const setDeploymentId = useContextStore((state) => state.setDeploymentId)
  const setWorkspace = useContextStore((state) => state.setWorkspace)
  const setDeployments = useContextStore((state) => state.setDeployments)
  const { canDeploy } = usePermissions()
  const { refreshProfile } = useAuth()
  const scopedInstance = isConsoleScopedInstance()

  const selectedOrganization = organizations.find((item) => item.id === org)
  const selectedDeployment = deployments.find((item) => item.id === deploymentId)
  const currentKind = normalizeDeploymentKind(selectedDeployment?.deploymentKind)
  const kindLabel = (kind: DeploymentKind) =>
    t(`contextSwitcher.deploymentKind.${kind}`)

  const resetScopedRoute = () => {
    setCurrentSessionId(null)
    if (location.pathname.startsWith('/operate')) {
      navigate('/operate/chat')
    } else {
      navigate(location.pathname)
    }
    setOpen(false)
  }

  const handleOrgChange = (nextOrg: string) => {
    setOrg(nextOrg || null)
    setWorkspace(null)
    resetScopedRoute()
  }

  const handleDeploymentChange = (nextDeploymentId: string) => {
    setDeploymentId(nextDeploymentId || null)
    setWorkspace(null)
    resetScopedRoute()
  }

  const resetCreateDialog = () => {
    setCreateOpen(false)
    setNewDeploymentName('')
    setNewDeploymentDescription('')
  }

  const handleCreateDeployment = async () => {
    const orgId = org?.trim()
    const name = newDeploymentName.trim()
    const description = newDeploymentDescription.trim()

    if (!orgId) {
      toast.error(t('contextSwitcher.createDeploymentNoOrganization'))
      return
    }

    if (!name) {
      toast.error(t('contextSwitcher.createDeploymentNameRequired'))
      return
    }

    setCreatingDeployment(true)
    try {
      const deployment = await deploymentsAPI.createDeployment(orgId, {
        name,
        description: description || undefined,
        environment: 'development',
        deployment_kind: 'sandbox',
      })

      await refreshProfile()
      // Optimistic insert: the refreshed memberships may not include the
      // brand-new deployment yet. Registering it in the store keeps the
      // selection resolvable and avoids the context sync falling back to
      // another deployment (which used to cause an URL/store refresh loop).
      const contextState = useContextStore.getState()
      if (!contextState.deployments.some((item) => item.id === deployment.id)) {
        contextState.setDeployments([...contextState.deployments, deployment])
      }
      setDeploymentId(deployment.id)
      setWorkspace(null)
      setCurrentSessionId(null)
      setOpen(false)
      resetCreateDialog()
      // Navigate with explicit context params: an URL without them would be
      // rewritten by the context sync effect using a possibly stale pathname,
      // which used to bounce the user away from the Agent Spec they just opened.
      navigate(agentSpecAuthoringHref(orgId, deployment.id))
      toast.success(
        t('contextSwitcher.createDeploymentSuccess', { name: deployment.name }),
      )
    } catch (createError) {
      const errorMessage =
        createError instanceof Error ? createError.message : ''
      const is409 =
        errorMessage.includes('409') ||
        errorMessage.includes('Conflict') ||
        errorMessage.includes('already exists')
      toast.error(
        is409
          ? t('contextSwitcher.createDeploymentConflict')
          : errorMessage || t('contextSwitcher.createDeploymentFailed'),
      )
    } finally {
      setCreatingDeployment(false)
    }
  }

  const handleConfirmKindChange = async () => {
    const orgId = org?.trim()
    const agentId = deploymentId?.trim()
    if (!orgId || !agentId || !pendingKind || !selectedDeployment) return

    setUpdatingKind(true)
    try {
      const result = await deploymentsAPI.updateDeploymentRuntime(orgId, agentId, {
        deployment_kind: pendingKind,
      })
      const nextKind = normalizeDeploymentKind(
        result.deployment.deploymentKind ?? pendingKind,
      )
      setDeployments(
        deployments.map((item) =>
          item.id === agentId
            ? { ...item, ...result.deployment, deploymentKind: nextKind }
            : item,
        ),
      )
      await refreshProfile()
      setPendingKind(null)
      toast.success(
        t('contextSwitcher.deploymentKindUpdated', {
          name: selectedDeployment.name,
          kind: kindLabel(nextKind),
        }),
      )
    } catch (updateError) {
      const message =
        updateError instanceof Error ? updateError.message : String(updateError)
      toast.error(message || t('contextSwitcher.deploymentKindUpdateFailed'))
    } finally {
      setUpdatingKind(false)
    }
  }

  const confirmVariant =
    pendingKind === 'production' || pendingKind === 'staging'
      ? 'destructive'
      : 'warning'

  return (
    <>
      <div className="relative">
        <button
          onClick={() => setOpen(!open)}
          className="flex max-w-[18rem] items-center gap-3 rounded-2xl border border-border/80 bg-card/90 px-3.5 py-2.5 text-sm shadow-sm backdrop-blur-sm transition-colors hover:bg-accent/70"
          data-testid="context-switcher-toggle"
        >
          <div className="flex min-w-0 flex-col items-start gap-0.5">
            <span className="truncate font-semibold">
              {selectedOrganization?.name || t('contextSwitcher.selectOrganization')}
            </span>
            <span className="truncate text-xs text-muted-foreground">
              {selectedDeployment?.name || t('contextSwitcher.selectDeployment')}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {selectedDeployment ? (
              <span
                className={cn(
                  'rounded px-2 py-1 text-xs font-medium',
                  deploymentKindBadgeClass(currentKind),
                )}
                data-testid="context-switcher-kind-badge"
              >
                {kindLabel(currentKind)}
              </span>
            ) : env ? (
              <span className="rounded bg-agent-cyan/20 px-2 py-1 text-xs text-agent-cyan">
                {env}
              </span>
            ) : null}
            <ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
          </div>
        </button>

        {open && (
          <div className="absolute left-0 top-full z-50 mt-2 w-80 space-y-3 rounded-md border border-border bg-popover p-3 shadow-lg">
            <div className="space-y-1">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t('contextSwitcher.organization')}
              </label>
              {scopedInstance ? (
                <div className="w-full rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-foreground">
                  {selectedOrganization?.name || t('contextSwitcher.notSelected')}
                </div>
              ) : (
                <select
                  value={org || ''}
                  onChange={(e) => handleOrgChange(e.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                >
                  <option value="">{t('contextSwitcher.selectOrganization')}</option>
                  {organizations.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('contextSwitcher.deployment')}
                </label>
                {canDeploy() && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setCreateOpen(true)}
                    data-testid="deployment-create-button"
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    {t('contextSwitcher.createDeployment')}
                  </Button>
                )}
              </div>
              <select
                value={deploymentId || ''}
                onChange={(e) => handleDeploymentChange(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                disabled={deployments.length === 0}
              >
                <option value="">{t('contextSwitcher.selectDeployment')}</option>
                {deployments.map((item) => {
                  const kind = normalizeDeploymentKind(item.deploymentKind)
                  return (
                    <option key={item.id} value={item.id}>
                      {item.name} ({kindLabel(kind)})
                    </option>
                  )
                })}
              </select>
            </div>

            {selectedDeployment ? (
              <div className="space-y-1">
                <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('contextSwitcher.deploymentKindLabel')}
                </label>
                <select
                  value={currentKind === 'unknown' ? '' : currentKind}
                  onChange={(e) => {
                    const next = normalizeDeploymentKind(e.target.value)
                    if (!e.target.value || next === currentKind) return
                    setPendingKind(next)
                  }}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  disabled={!canDeploy() || updatingKind}
                  data-testid="context-switcher-kind-select"
                >
                  {currentKind === 'unknown' ? (
                    <option value="">{kindLabel('unknown')}</option>
                  ) : null}
                  {DEPLOYMENT_KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {kindLabel(kind)}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-muted-foreground">
                  {t('contextSwitcher.deploymentKindHint')}
                </p>
              </div>
            ) : null}

            <div className="rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              <div>{t('contextSwitcher.orgId')}: {org || t('contextSwitcher.notSelected')}</div>
              <div>{t('contextSwitcher.deployment')}: {selectedDeployment?.name || t('contextSwitcher.notSelected')}</div>
              <div>
                {t('contextSwitcher.deploymentKindLabel')}:{' '}
                {selectedDeployment
                  ? kindLabel(currentKind)
                  : t('contextSwitcher.notSelected')}
              </div>
              <div>{t('contextSwitcher.environment')}: {env || t('contextSwitcher.notSelected')}</div>
            </div>

            {isLoading && (
              <div className="text-xs text-muted-foreground">{t('contextSwitcher.loadingContext')}</div>
            )}

            {error && (
              <div className="text-xs text-red-600">{error}</div>
            )}
          </div>
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('contextSwitcher.createDeploymentTitle')}</DialogTitle>
            <DialogDescription>
              {t('contextSwitcher.createDeploymentDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">
                {t('contextSwitcher.createDeploymentNameLabel')}
              </label>
              <Input
                value={newDeploymentName}
                onChange={(e) => setNewDeploymentName(e.target.value)}
                placeholder={t('contextSwitcher.createDeploymentNamePlaceholder')}
                disabled={creatingDeployment}
                data-testid="deployment-name-input"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">
                {t('contextSwitcher.createDeploymentNotesLabel')}
              </label>
              <textarea
                value={newDeploymentDescription}
                onChange={(e) => setNewDeploymentDescription(e.target.value)}
                placeholder={t('contextSwitcher.createDeploymentNotesPlaceholder')}
                disabled={creatingDeployment}
                rows={4}
                className="flex min-h-[112px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={resetCreateDialog}
              disabled={creatingDeployment}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => void handleCreateDeployment()}
              disabled={creatingDeployment}
              data-testid="deployment-create-submit"
            >
              {creatingDeployment
                ? t('contextSwitcher.createDeploymentSubmitting')
                : t('contextSwitcher.createDeploymentConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={pendingKind !== null}
        onOpenChange={(next) => {
          if (!next && !updatingKind) setPendingKind(null)
        }}
        title={t('contextSwitcher.deploymentKindConfirmTitle')}
        description={
          pendingKind
            ? t('contextSwitcher.deploymentKindConfirmDescription', {
                name: selectedDeployment?.name ?? '',
                from: kindLabel(currentKind),
                to: kindLabel(pendingKind),
              })
            : undefined
        }
        confirmText={
          updatingKind
            ? t('contextSwitcher.deploymentKindUpdating')
            : t('contextSwitcher.deploymentKindConfirmAction')
        }
        cancelText={t('common.cancel')}
        variant={confirmVariant}
        disabled={updatingKind}
        onConfirm={() => void handleConfirmKindChange()}
      />
    </>
  )
}
